import React, { FC } from 'react';

import PropTypes from 'prop-types';

import {
  logout,
  getOxygenUrl,
  getAccHostname,
} from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { clearNetworkCache } from '@adsk/alloy-helpers';
import A from '@adsk/alloy-react-a';
import Button from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import { ANALYTICS_TYPES } from '../helpers/analytics';
import getNotificationSettingsUrl from '../helpers/getNotificationSettingsUrl';
import { userShape } from '../helpers/propTypes';
import { User } from '../helpers/types';

type ProfileProps = {
  showSubscriptionsModal?: boolean;
  user: User;
  projectId?: string;
  onLogout?: () => void;
  onClose: () => void;
  onOpenSubscriptions: () => void;
};

/**
 * Acs TopNavigation
 */
const Profile: FC<ProfileProps> = ({
  showSubscriptionsModal,
  user,
  projectId,
  onLogout,
  onClose,
  onOpenSubscriptions,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  return (
    <div
      css={[
        {
          minWidth: '250px',
          padding: '24px',
        },
      ]}
    >
      <div
        css={[theme.typography.heading4, { color: theme.colors.charcoal900 }]}
      >
        {user.firstName} {user.lastName}
      </div>
      <div
        css={[theme.typography.bodySmall, { color: theme.colors.charcoal600 }]}
      >
        {user.email}
      </div>
      {showSubscriptionsModal && (
        <div
          css={[
            theme.typography.bodySmall,
            { color: theme.colors.charcoal600, marginTop: 10 },
          ]}
        >
          <a
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onClose();
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_MYSUBSCRIPTIONS_CLICK,
              );
              onOpenSubscriptions();
            }}
            data-testid="My__Subscriptions__Label"
            rel="noopener noreferrer"
          >
            {T.translate('MY_SUBSCRIPTIONS')}
          </a>
        </div>
      )}

      {projectId && (
        <div
          css={[
            theme.typography.bodySmall,
            { color: theme.colors.charcoal600, marginTop: 10 },
          ]}
        >
          <A
            href={getNotificationSettingsUrl(projectId)}
            rel="noopener noreferrer"
            onClick={() =>
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_NOTIFICATIONSETTINGS_CLICK,
              )
            }
          >
            {T.translate('NOTIFICATION_SETTINGS')}
          </A>
        </div>
      )}

      <div css={[{ paddingTop: '32px' }]}>
        <Button
          as="a"
          href={getOxygenUrl()}
          rel="noopener noreferrer"
          target="_blank"
          variant={Button.VARIANTS.PRIMARY}
          size={Button.SIZES.SMALL}
          style={{ marginRight: 12 }}
          onClick={() => {
            analytics.track(
              ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_PROFILESETTINGS_CLICK,
            );
            onClose();
          }}
        >
          {T.translate('PROFILE_SETTINGS')}
        </Button>
        <Button
          variant={Button.VARIANTS.SECONDARY}
          size={Button.SIZES.SMALL}
          onClick={() => {
            clearNetworkCache();
            analytics.track(
              ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_SIGNOUT_CLICK,
            );
            if (onLogout) {
              onLogout();
            }
            logout(getAccHostname());
            onClose();
          }}
        >
          {T.translate('SIGN_OUT')}
        </Button>
      </div>
    </div>
  );
};

Profile.displayName = 'Profile';

Profile.propTypes = {
  /** show subscriptions modal (page level) */
  showSubscriptionsModal: PropTypes.bool,
  /** current user */
  user: userShape,
  /** current selected project id */
  projectId: PropTypes.string,
  /** callback called on logout */
  onLogout: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func.isRequired,
  /** opens my subscriptions modal */
  onOpenSubscriptions: PropTypes.func.isRequired,
};

export default Profile;
